import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

const rwf = (n) => `RWF ${Number(n ?? 0).toLocaleString()}`
const dur = (s) => { if (!s) return '—'; const m = Math.floor(s / 60); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m ${s % 60}s` }
const fmt = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

// Total earnings for a stream = product-sale revenue + kudos/tips received.
const earnings = (s = {}) => Number(s.revenue || 0) + Number(s.tip_total || 0)

// A replay only exists once the upload has finished — recording_status can
// lag/mismatch recording_url in edge cases, so require both.
const isReplayReady = (s = {}) => s.recording_status === 'ready' && !!s.recording_url

const PER_PAGE = 15

function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}

// ── Replay panel — view/upload/delete a stream's recorded replay ─────────────
function ReplayPanel({ stream, onUpdate }) {
  const fileRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [progress,  setProgress]  = useState(0)
  const [deleting,  setDeleting]  = useState(false)

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = '' // allow re-selecting the same file after a failed attempt
    if (!file) return
    setUploading(true); setProgress(0)
    try {
      const fd = new FormData()
      fd.append('recording', file)
      const res = await api.post(`/live/${stream.live_id}/recording`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => evt.total && setProgress(Math.round((evt.loaded / evt.total) * 100)),
      })
      onUpdate({ recording_url: res.data.recordingUrl, recording_status: 'ready' })
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload replay')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this replay? Viewers will no longer be able to watch it back.')) return
    setDeleting(true)
    try {
      await api.delete(`/live/${stream.live_id}/recording`)
      onUpdate({ recording_url: null, recording_status: 'none' })
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete replay')
    } finally {
      setDeleting(false)
    }
  }

  if (isReplayReady(stream)) {
    return (
      <div style={{ marginTop: 12 }}>
        <video
          src={stream.recording_url}
          controls
          playsInline
          poster={stream.thumbnail_url || undefined}
          style={{ width: '100%', maxHeight: 360, borderRadius: 8, background: '#000', display: 'block' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{ height: 30, padding: '0 12px', borderRadius: 7, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: deleting ? 0.6 : 1 }}>
            {deleting ? 'Deleting…' : '🗑 Delete Replay'}
          </button>
        </div>
      </div>
    )
  }

  const failed = stream.recording_status === 'failed'
  return (
    <div style={{ marginTop: 12, background: '#fff', border: '1px dashed #e9e9e4', borderRadius: 8, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: failed ? '#b91c1c' : '#111317' }}>{failed ? 'Replay upload failed' : 'No replay uploaded'}</div>
        <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2 }}>
          {uploading ? `Uploading… ${progress}%` : 'Upload the recording captured during this stream so viewers can watch it back.'}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleUpload} />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        style={{ height: 34, padding: '0 16px', borderRadius: 7, border: 'none', background: '#111317', color: '#fff', fontSize: 12, fontWeight: 700, cursor: uploading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: uploading ? 0.6 : 1, flexShrink: 0 }}>
        {uploading ? `${progress}%` : (failed ? '↻ Retry Upload' : '⬆ Upload Replay')}
      </button>
    </div>
  )
}

export default function LiveHistory() {
  const [streams,    setStreams]    = useState([])
  const [summary,    setSummary]    = useState(null)
  const [loading,    setLoading]    = useState(true)
  const [selected,   setSelected]   = useState(null)
  const [shopId,     setShopId]     = useState(null)
  const [page,       setPage]       = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total,      setTotal]      = useState(0)
  const fetchRef = useRef(0)

  // Get seller's shop ID once
  useEffect(() => {
    api.get('/shops/my-shop')
      .then(res => setShopId(res.data.shop?.shop_id || res.data.shop_id))
      .catch(() => {})
  }, [])

  // Fetch summary once when shopId is known
  useEffect(() => {
    if (!shopId) return
    api.get(`/live-history/${shopId}/summary`)
      .then(res => setSummary(res.data))
      .catch(() => setSummary(null))
  }, [shopId])

  // Fetch paginated streams
  const fetchStreams = useCallback(async () => {
    if (!shopId) return
    const id = ++fetchRef.current
    setLoading(true)
    try {
      const offset = (page - 1) * PER_PAGE
      const res = await api.get(`/live-history/${shopId}?limit=${PER_PAGE}&offset=${offset}`)
      if (id !== fetchRef.current) return
      const data = res.data
      // Backend returns { history, count } using offset-based pagination
      const rows = data.history || data.streams || data || []
      const count = data.count ?? rows.length
      setStreams(rows)
      setTotal(count)
      setTotalPages(Math.max(1, Math.ceil(count / PER_PAGE)))
    } catch {
      if (id !== fetchRef.current) return
      setStreams([])
    } finally {
      if (id === fetchRef.current) setLoading(false)
    }
  }, [shopId, page])

  useEffect(() => { fetchStreams() }, [fetchStreams])

  // Close detail panel when page changes
  useEffect(() => { setSelected(null) }, [page])

  // Patch a single stream in place (used after replay upload/delete) so the
  // table + expanded panel reflect the change without a full refetch.
  const patchStream = (liveId, patch) =>
    setStreams(prev => prev.map(s => s.live_id === liveId ? { ...s, ...patch } : s))

  const s = summary || {}

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Live History</div>
        <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Performance breakdown of your past live sessions.</div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Total Sessions" value={Number(s.totalStreams ?? total).toLocaleString()} />
        <StatCard label="Total Viewers"  value={Number(s.avgPeakViewers ?? 0).toLocaleString()} accent="#3b82f6" />
        <StatCard label="Total Messages" value={Number(s.totalMessages ?? 0).toLocaleString()} accent="#111317" />
        <StatCard label="All-time Peak"  value={Number(s.allTimePeakViewers ?? 0).toLocaleString()} accent="#E8440A" />
        <StatCard label="Total Earnings" value={rwf(Number(s.totalRevenue || 0) + Number(s.totalTips || 0))} accent="#15803d" />
      </div>

      {/* Streams table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, overflow: 'hidden' }}>
        {/* Table header */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 0.9fr 0.7fr', padding: '10px 20px', background: '#f9f9f7', borderBottom: '1px solid #e9e9e4' }}>
          {['Date / Channel', 'Duration', 'Peak Viewers', 'Messages', 'Replay', ''].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading streams…</div>
        ) : streams.length === 0 ? (
          <div style={{ padding: '56px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🎥</div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>No live sessions yet</div>
            <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Go live from the mobile app to start selling.</div>
          </div>
        ) : streams.map((stream, i) => {
          const isSelected = selected?.live_id === stream.live_id
          return (
            <div key={stream.live_id || i}>
              <div
                onClick={() => setSelected(isSelected ? null : stream)}
                style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 0.9fr 0.7fr', padding: '13px 20px', borderBottom: '1px solid #f3f3f0', cursor: 'pointer', alignItems: 'center', transition: 'background 0.12s', background: isSelected ? '#fafaf8' : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                onMouseLeave={e => e.currentTarget.style.background = isSelected ? '#fafaf8' : 'transparent'}>

                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{fmt(stream.started_at)}</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{stream.agora_channel || '—'}</div>
                </div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{dur(stream.duration_secs)}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, color: '#3b82f6' }}>{stream.peak_viewers ?? 0}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{stream.total_messages ?? 0}</div>
                <div>
                  {isReplayReady(stream)
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 800, color: '#e8440a', background: '#fff3f0', border: '1px solid #fdd0c0', borderRadius: 4, padding: '2px 7px', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.04em' }}>▶ Replay</span>
                    : <span style={{ fontSize: 12, color: '#c8c0c0' }}>—</span>
                  }
                </div>
                <div style={{ fontSize: 11, color: '#9a9a94' }}>{isSelected ? '▲ Hide' : '▼ Details'}</div>
              </div>

              {/* Inline expanded detail */}
              {isSelected && (
                <div style={{ background: '#f9f9f7', borderBottom: '1px solid #f3f3f0', padding: '14px 20px', animation: 'fadeIn 0.15s ease' }}>
                  <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}`}</style>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                    {[
                      ['Started',   fmt(stream.started_at)],
                      ['Ended',     fmt(stream.ended_at)],
                      ['Duration',  dur(stream.duration_secs)],
                      ['Peak',      stream.peak_viewers ?? 0],
                      ['Messages',  stream.total_messages ?? 0],
                      ['Orders',    stream.order_count ?? 0],
                      ['Kudos (Tips)', rwf(stream.tip_total)],
                      ['Earnings',  rwf(earnings(stream))],
                    ].map(([k, v]) => (
                      <div key={k} style={{ background: '#fff', borderRadius: 8, padding: '9px 12px', border: '1px solid #e9e9e4' }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 3 }}>{k}</div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  <ReplayPanel stream={stream} onUpdate={patch => patchStream(stream.live_id, patch)} />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
            Page {page} of {totalPages} · {total} sessions
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}
    </div>
  )
}
